import { V3_API_URL } from 'views/TradingCompetitionV3/config'

/**
 * Alerts (web push) client, two tiers (2026-10-07). The CADINU API (app `webpush`) holds the VAPID key and one row per device; it
 * lives next to the social API (stage-api on Stage, api.cadinu.io on production).
 *  - PUBLIC: subscribeDevice / unsubscribeDevice / deviceStatus send NO Authorization header. The endpoint is the device's own secret,
 *    so only this browser can remove it. The connected wallet (if any) goes along as an unverified wallet, for public notices.
 *  - PRIVATE: linkDevice uses an EXISTING claimed-profile session (the profile's one free signature, made earlier). Nothing in this
 *    file asks a wallet to sign.
 */
export const PUSH_API = process.env.NEXT_PUBLIC_PUSH_API_URL || `${new URL(V3_API_URL).origin}/api/webpush`

export interface PushConfig {
  enabled: boolean
  publicKey: string | null
}

/** what the server knows about this device */
export interface DeviceState {
  subscribed: boolean
  verified: boolean
  wallet: string | null
}

export class PushApiError extends Error {
  constructor(public status: number, code: string) {
    super(code)
  }
}

export async function getPushConfig(): Promise<PushConfig> {
  const r = await fetch(`${PUSH_API}/config`, { headers: { Accept: 'application/json' } })
  if (!r.ok) return { enabled: false, publicKey: null }
  const j = await r.json().catch(() => ({}))
  return { enabled: !!j.enabled && typeof j.publicKey === 'string', publicKey: typeof j.publicKey === 'string' ? j.publicKey : null }
}

export function urlBase64ToUint8Array(b64: string): Uint8Array {
  const padded = (b64 + '='.repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

async function call(method: string, path: string, body: unknown, token?: string): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `Token ${token}`
  const r = await fetch(`${PUSH_API}/${path}`, { method, headers, body: JSON.stringify(body) })
  if (!r.ok) {
    const j = await r.json().catch(() => ({}))
    throw new PushApiError(r.status, (j && j.error) || `http_${r.status}`)
  }
  return r
}

const toState = (j: any): DeviceState => ({ subscribed: !!j?.subscribed, verified: !!j?.verified, wallet: typeof j?.wallet === 'string' ? j.wallet : null })

function device(sub: PushSubscription) {
  const j = sub.toJSON()
  return { endpoint: j.endpoint, keys: { p256dh: j.keys?.p256dh, auth: j.keys?.auth } }
}

/** PUBLIC tier: store this device (no signature, no session). */
export async function subscribeDevice(sub: PushSubscription, wallet?: string | null): Promise<DeviceState> {
  const r = await call('POST', 'subscriptions', wallet ? { ...device(sub), wallet } : device(sub))
  return toState(await r.json().catch(() => ({})))
}

/** PUBLIC tier: remove this device by its endpoint. false = the server did not know it. */
export async function unsubscribeDevice(endpoint: string): Promise<boolean> {
  try {
    await call('DELETE', 'subscriptions', { endpoint })
    return true
  } catch (e) {
    if (e instanceof PushApiError && e.status === 404) return false
    throw e
  }
}

/** What the server knows about this device (POST: the endpoint never goes into a URL). */
export async function deviceStatus(endpoint: string): Promise<DeviceState> {
  const r = await call('POST', 'subscriptions/status', { endpoint })
  return toState(await r.json().catch(() => ({})))
}

/** PRIVATE tier: link this device to the wallet of an existing CADINU session (no new signature). */
export async function linkDevice(token: string, sub: PushSubscription): Promise<DeviceState> {
  const r = await call('POST', 'subscriptions/link', device(sub), token)
  return toState(await r.json().catch(() => ({})))
}

type Registration = Pick<ServiceWorkerRegistration, 'pushManager'>
const askPermission = () => (typeof Notification !== 'undefined' ? Notification.requestPermission() : Promise.resolve('denied' as NotificationPermission))

/**
 * Turn alerts on. Call ONLY from a tap: it asks for notification permission (browsers require a user gesture), subscribes this
 * browser with the server's VAPID key (re-subscribing if an old subscription used another key), stores the device publicly and,
 * when a claimed-profile session token is given, links it for personal alerts. An expired session leaves the public alerts on.
 */
export async function enableAlerts(opts: {
  registration: Registration
  publicKey: string
  wallet?: string | null
  token?: string | null
  requestPermission?: () => Promise<NotificationPermission>
}): Promise<{ result: 'on' | 'denied' | 'default'; state?: DeviceState }> {
  const permission = await (opts.requestPermission ?? askPermission)()
  if (permission !== 'granted') return { result: permission === 'denied' ? 'denied' : 'default' }
  const key = urlBase64ToUint8Array(opts.publicKey)
  let sub = await opts.registration.pushManager.getSubscription()
  const current = sub?.options?.applicationServerKey
  if (sub && current && !sameKey(new Uint8Array(current), key)) {
    await sub.unsubscribe().catch(() => false)
    sub = null
  }
  if (!sub) sub = await opts.registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
  let state = await subscribeDevice(sub, opts.wallet)
  if (opts.token) state = await linkDevice(opts.token, sub).catch(() => state)
  return { result: 'on', state }
}

/** Turn alerts off: the device is removed on the server by its endpoint (no auth), and this browser's subscription is dropped. */
export async function disableAlerts(opts: { registration: Registration }) {
  const sub = await opts.registration.pushManager.getSubscription()
  if (!sub) return
  await unsubscribeDevice(sub.endpoint)
  await sub.unsubscribe().catch(() => false)
}

function sameKey(a: Uint8Array, b: Uint8Array) {
  return a.length === b.length && a.every((v, i) => v === b[i])
}
